import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import ExportDropdown from "../../../Components/Export"
import Filters from "../../../Components/Filters"
import GlobalFilterTestData from "../../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../../Components/ManageColumns"
import Table from "../../../Components/Table"
import { getBaseColumns } from "../../../Components/Table/baseColumns"
import { AdminRoutesPaths } from "../../../constants/admin-routes"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { defaultFiltersByFolders, serializeData, useQuery } from "../../../Utils/utils"
import {
  addGlobalFilter as addGlobalFilterAction,
  clearGlobalFilters as clearGlobalFiltersAction,
  removeGlobalFilter as removeGlobalFilterAction
} from "../../GlobalFilters/redux/globalFilters.slice"
import RequestsCreators, { selectRequestResult } from "../reducer"
import ProfileDetailsModal from "./components/ProfileDetailsModal"
import ProfileUpdateModal from "./components/ProfileUpdateModal"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import { isProfileUpdateRow, PROFILE_UPDATE_STATUSES } from "./constants/profileUpdate"
import { ROW_ACTIONS } from "./constants/rowActions"
import {
  clearFilters as clearReceiptsFiltersAction,
  pinnedCols,
  selectAreFiltersDefault,
  setProp as setReceiptsProp
} from "./redux/requestsReceipts.slice"
import {
  deleteRequestsReceipt,
  getRequestsReceipts,
  getRequestsReceiptsExport,
  getRequestsReceiptsFilterOptions,
  updateReceiptProfileStatus,
  updateReceiptStatus,
  updateRequestsReceipt
} from "./redux/requestsReceipts.thunks"

import "./styles.less"

const pageSize = 50

const { Text } = Typography

const defaultFiltersByFolder = defaultFiltersByFolders["receipts"]
const PROFILE_UPDATE_TABS = new Set(PROFILE_UPDATE_STATUSES)

const RequestsReceiptsPage = ({
  isMobile,
  businessAccountId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  filterOptions,
  exportFetching,
  exportResult,
  statistics,
  test_data,
  ...props
}) => {
  const prevTestDataRef = useRef(test_data)
  const listParamsRef = useRef(null)
  const currentPageRowCountRef = useRef(0)
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  const query = useQuery(search)
  const status = query.get("status")
  const isProfileUpdateTab = PROFILE_UPDATE_TABS.has(status)
  const { t } = useTranslation()

  const { notification, modal } = App.useApp()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState("-created_timestamp")
  const [profileDetailsId, setProfileDetailsId] = useState(null)
  const [profileUpdateId, setProfileUpdateId] = useState(null)

  const isFiltersDefault = useSelector((state) => selectAreFiltersDefault(state, status))
  const requestResult = useSelector(selectRequestResult)

  const { count, results } = result
  currentPageRowCountRef.current = results.length

  const availableFilters = isProfileUpdateTab
    ? defaultColumnsAdmin.filter(({ field_id }) => field_id !== "request_status")
    : defaultColumnsAdmin
  const visibleFilters = isProfileUpdateTab
    ? { ...visibleFiltersAdmin, request_status: false }
    : visibleFiltersAdmin

  const myColumnsLength = columnsAdmin.filter(({ active }) => active).length
  const myColumnsHash = columnsAdmin
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")
  const filterOptionsStatusHash = (filterOptions?.["request_status"] || []).join("")

  const statisticsFolder = statistics?.["receipts_counter"]?.[status || "all"] || 0

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const handleClearFilters = () => {
    const newFilters = status
      ? defaultFiltersByFolder[status]
      : { request_status: "all", is_archived: null, is_unassigned: null }
    props.clearFilters(newFilters)
    props.clearGlobalFilters()
  }

  const tableSettings = useMemo(() => {
    const viewportHeight = window.innerHeight
    const fixedPixels = 72 + 24 + 32 + 24 + 43 + (16 + 32 + 16) + 16
    return {
      scroll: {
        x: 250 * columnsAdmin.filter(({ active }) => active).length,
        y: viewportHeight - fixedPixels,
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !statisticsFolder ? (
          <EmptyComponent type="requests_receipts_admin" />
        ) : (
          <EmptyComponent type="search">
            <Button
              className="flex"
              icon={<Icon type={"menu_search"} />}
              onClick={handleClearFilters}
              data-testid="deflect-admin-clear-search-btn"
            >
              Clear search
            </Button>
          </EmptyComponent>
        )
      }
    }
  }, [myColumnsLength, count, pageNumber, statisticsFolder])

  const handleChangeStatus = async ({ record, newStatus }) => {
    const applyStatus =
      record.source === "profile_update" ? props.updateProfileStatus : props.updateDescriptorStatus

    try {
      await applyStatus({ id: record.id, status: newStatus }).unwrap()
      const currentPage = listParamsRef.current?.page || 1

      if (currentPageRowCountRef.current === 1 && currentPage > 1) {
        setPageNumber(currentPage - 1)
      } else if (listParamsRef.current) {
        props.getInfo(listParamsRef.current)
      }
    } catch {
      // Global error notification already shown from thunk.
    }
  }

  const filterOptionsByFolder = useMemo(
    () => ({
      ...filterOptions,
      request_status:
        !status || defaultFiltersByFolder[status]?.request_status === "all"
          ? filterOptions?.request_status
          : defaultFiltersByFolder[status]?.request_status
    }),
    [filterOptions, status]
  )

  const getTableColumns = useMemo(() => {
    const options = filterOptions?.["request_status"] || []

    const allColumns = {
      ...getBaseColumns({ onSelectRow: handleSelectRow, isAdmin: true }),
      ...getColumns({ options, handleChangeStatus, onSelectRow: handleSelectRow })
    }
    return [
      allColumns["status"],
      ...columnsAdmin
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myColumnsHash, filterOptionsStatusHash, search])

  const handleChangeTable = useCallback((pagination, _filters, sorter) => {
    let orderingNew = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        orderingNew = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else if (sorter.order) {
        orderingNew = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
      }
    }
    setPageNumber(pagination.current)
    setOrdering(orderingNew || "-created_timestamp")
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (newData?.user_email) {
      if (Array.isArray(newData.user_email) && newData.user_email.length > 0) {
        newData.user_email.forEach((email) => {
          if (email) props.addGlobalFilter("user_email", email.trim())
        })
      } else if (typeof newData.user_email === "string" && newData.user_email.trim()) {
        props.addGlobalFilter("user_email", newData.user_email.trim())
      } else {
        props.removeGlobalFilter("user_email")
      }
    }
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleSelectRow(data, type) {
    const businessAccountId = data?.business_account?.id

    switch (type) {
      case ROW_ACTIONS.BUSINESS_ACCOUNT: {
        if (businessAccountId) {
          navigate(
            `${AdminRoutesPaths.receipts.all}?${new URLSearchParams({ business_account_id: businessAccountId })}`
          )
        }
        return
      }

      case ROW_ACTIONS.DASHBOARD: {
        navigate(`/admin/dashboard?business_account_id=${businessAccountId}`)
        return
      }

      case ROW_ACTIONS.PROFILE_DETAILS: {
        if (isProfileUpdateRow(data)) {
          setProfileUpdateId(data.id)
        } else {
          setProfileDetailsId(data.id)
        }
        return
      }

      case ROW_ACTIONS.ADD_COMMENT: {
        props.selectRequestAddComment(data)
        const searchParams = new URLSearchParams(search)
        searchParams.set("add_comment", data?.id)
        navigate(`?${searchParams.toString()}`)
        return
      }

      case ROW_ACTIONS.ARCHIVE: {
        const nextArchived = !data?.["is_archived"]
        try {
          await props.updateStatus({ id: data.id, is_archived: nextArchived }).unwrap()
          const currentPage = listParamsRef.current?.page || 1
          if (currentPageRowCountRef.current === 1 && currentPage > 1) {
            setPageNumber(currentPage - 1)
          } else if (listParamsRef.current) {
            props.getInfo(listParamsRef.current)
          }
          if (nextArchived) {
            notification.success({
              message: "Archiving complete",
              ...notificationConfig,
              style: { minWidth: "max-content" }
            })
          }
        } catch {
          // Global error notification already shown from thunk.
        }
        return
      }

      case ROW_ACTIONS.DELETE: {
        modal.confirm({
          ...modalConfig("exclamation"),
          centered: !isMobile,
          title: "Confirm deleting",
          content: "Are you sure you want to delete this request? This action can NOT be undone.",
          cancelText: "Cancel",
          okText: "Delete",
          okButtonProps: {
            ...modalConfig("exclamation")?.okButtonProps,
            danger: true,
            ghost: true,
            "data-testid": "deflect-admin-delete-confirm-btn"
          },
          cancelButtonProps: {
            ...modalConfig("exclamation")?.cancelButtonProps,
            "data-testid": "deflect-admin-delete-cancel-btn"
          },
          async onOk() {
            try {
              await props.deleteRequest(data.id).unwrap()
              notification.success({
                message: "Request deleted",
                ...notificationConfig
              })
            } catch {
              // Global error notification already shown from thunk (handleApiError).
            }
          },
          onCancel() {
            // noop
          }
        })
        return
      }

      case ROW_ACTIONS.USER_BALANCE: {
        navigate(
          `/users/user_balance?user_id=${data?.user_id}&from=${encodeURIComponent(pathname + search)}`
        )
        return
      }

      default:
        return
    }
  }

  // `export_mode` tells the backend which button this was. The All folder sends no status, which
  // the table reads as descriptor rows only; without the mode the export read the same query as
  // "both row types", so Export as filtered carried every profile update too (CHA1-2162).
  const handleExport = useCallback(
    (type) => {
      const serializeFilters = serializeData(filters)
      props.getExport({
        ...(type !== "all" && serializeFilters),
        ...(type !== "all" ? { test_data } : {}),
        export_mode: type === "all" ? "all" : "filtered"
      })
    },
    [filters, test_data]
  )

  useEffect(() => {
    document.title = "Requests Receipt - Chargeback"
  }, [])

  useEffect(() => {
    props.getFilterOptions({ test_data })
  }, [test_data])

  const buildFetchData = useCallback(() => {
    const serializeFilters = serializeData({
      ...filters,
      ...(status &&
        defaultFiltersByFolder[status] &&
        (!filters["request_status"]?.length || filters["request_status"] === "all") && {
          request_status: defaultFiltersByFolder[status]?.request_status
        })
    })

    const columns = columnsAdmin.filter(({ active }) => active).map(({ field_id }) => field_id)
    return {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      test_data,
      ...(columns.length && {
        columns: ["id", "request_status", "is_archived", "user_id", ...columns].join(",")
      })
    }
  }, [filters, status, columnsAdmin, pageNumber, ordering, test_data])

  useEffect(() => {
    if (requestResult) return

    const testDataChanged = prevTestDataRef.current !== test_data
    if (testDataChanged) {
      prevTestDataRef.current = test_data
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    const data = buildFetchData()
    listParamsRef.current = data
    props.getInfo(data)
  }, [businessAccountId, ordering, pageNumber, filters, test_data, myColumnsLength, requestResult])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: "Exporting",
        description:
          t(`${exportResult?.message}`) || t("Receipt requests will be downloaded shortly."),
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  useEffect(() => {
    if (status && defaultFiltersByFolder[status]) {
      props.changeFilters(defaultFiltersByFolder[status])
      if (pageNumber !== 1) setPageNumber(1)
    } else {
      props.changeFilters({ request_status: "all", is_archived: null, is_unassigned: null })
    }
  }, [status])

  return (
    <div className="requests-page" data-testid="deflect-admin-page">
      <Flex gap={32} align="center" justify="space-between" style={{ margin: "0 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptionsByFolder}
          withFolders={true}
          allFilters={availableFilters}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          filtersType={"receipts"}
          newTagStyle="newTagStyle"
          showClearBtn={!isFiltersDefault}
          onClearFilters={handleClearFilters}
          addGlobalFilter={props.addGlobalFilter}
          removeGlobalFilter={props.removeGlobalFilter}
        />

        <Space style={{ flexShrink: 0 }}>
          <GlobalFilterTestData />
          <ExportDropdown loading={exportFetching} onExport={handleExport} isAdmin={true} />
          <ManageColumn
            myColumns={columnsAdmin}
            onChangeColumns={handleChangeColumns}
            pinnedColumns={pinnedCols}
          />
        </Space>
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        tableLayout="auto"
        locale={tableSettings.locale}
        onRow={(record) => ({ "data-testid": `deflect-admin-row-${record.id}` })}
      />

      <ProfileDetailsModal
        open={profileDetailsId != null}
        id={profileDetailsId}
        onClose={() => setProfileDetailsId(null)}
      />

      <ProfileUpdateModal
        open={profileUpdateId != null}
        id={profileUpdateId}
        onClose={() => setProfileUpdateId(null)}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.requestsReceipts.visibleFiltersAdmin,
  defaultColumnsAdmin: state.requestsReceipts.defaultColumnsAdmin,
  columnsAdmin: state.requestsReceipts.columnsAdmin,

  fetching: state.requestsReceipts.fetching,
  result: state.requestsReceipts.result,

  filters: state.requestsReceipts.filters,
  filterOptions: state.requestsReceipts.filterOptions,
  fetchingFilterOptions: state.requestsReceipts.fetchingFilterOptions,

  exportFetching: state.requestsReceipts.exportFetching,
  exportResult: state.requestsReceipts.exportResult,

  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getFilterOptions: (params) => dispatch(getRequestsReceiptsFilterOptions(params)),
  getInfo: (params) => dispatch(getRequestsReceipts(params)),
  deleteRequest: (id) => dispatch(deleteRequestsReceipt(id)),
  updateStatus: (params) => dispatch(updateRequestsReceipt(params)),
  updateDescriptorStatus: (params) => dispatch(updateReceiptStatus(params)),
  updateProfileStatus: (params) => dispatch(updateReceiptProfileStatus(params)),
  getExport: (payload) => dispatch(getRequestsReceiptsExport(payload)),
  clearExportResult: () => dispatch(setReceiptsProp({ key: "exportResult", value: null })),
  changeColumns: (key, columns) => dispatch(setReceiptsProp({ key, value: columns })),
  changeFilters: (filters) => dispatch(setReceiptsProp({ key: "filters", value: filters })),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(setReceiptsProp({ key: visibleFiltersType, value: filters })),
  clearFilters: (data) => dispatch(clearReceiptsFiltersAction(data)),

  clearGlobalFilters: () => dispatch(clearGlobalFiltersAction()),
  addGlobalFilter: (key, value) => dispatch(addGlobalFilterAction({ key, value })),
  removeGlobalFilter: (key) => dispatch(removeGlobalFilterAction(key)),
  selectRequestAddComment: (request) =>
    dispatch(RequestsCreators.requestsSetProp("selectedRequest", request))
})

export default connect(mapStateToProps, mapDispatchToProps)(RequestsReceiptsPage)
